import { useEffect, useMemo, useState } from "react";

const CashToCloseControls = ({ Fields = [], fieldValues = {}, onFieldChange }) => {
    const [localFieldValues, setLocalFieldValues] = useState({});

    useEffect(() => {
        const sanitized = {};
        for (const field of Fields) {
            let val = fieldValues?.[field.id] ?? "";
            if (field.datatype === "date" && val === "//") val = "";
            if (field.format === "YN_BOOL") {
                if (val === "Y") val = "true";
                else if (val === "N") val = "false";
            }
            if (field.id === "4459" && (val === "" || val == null)) val = "N";
            sanitized[field.id] = val;
        }
        setLocalFieldValues(sanitized);
    }, [fieldValues, Fields]);

    const rows = useMemo(() => {
        const map = new Map();
        for (const f of Fields) {
            const rowKey = f?.layout?.row ?? `__row_${f.id}`;
            if (!map.has(rowKey)) map.set(rowKey, []);
            map.get(rowKey).push(f);
        }
        return map;
    }, [Fields]);

    const updateLocal = (fieldId, value) => {
        setLocalFieldValues((prev) => ({ ...prev, [fieldId]: value }));
        if (typeof onFieldChange === "function") onFieldChange(fieldId, value);
    };

    const renderControl = (field, value) => {
        if (!field) return null;
        if (field.control === "label" || field.control === "static") {
            return <div className="form-label block text-sm">{field.name}</div>;
        }
        if (field.control === "textbox" && field.datatype !== "date") {
            return (
                <input
                    id={`field-${field.id}`}
                    type="text"
                    className="form-input w-full text-right p-1 border rounded"
                    value={value ?? ""}
                    readOnly={!!field.readonly}
                    aria-label={field.name}
                    onChange={(e) => {
                        let newValue = e.target.value;
                        if (field.datatype === "decimal") {
                            if (/^\d*\.?\d*$/.test(newValue)) updateLocal(field.id, newValue);
                        } else updateLocal(field.id, newValue);
                    }}
                    data-field-id={field.id}
                />
            );
        }
        if (field.control === "dropdown") {
            return (
                <select
                    id={`field-${field.id}`}
                    className="form-input w-full text-sm border rounded p-1"
                    value={value ?? ""}
                    disabled={!!field.readonly}
                    onChange={(e) => updateLocal(field.id, e.target.value)}
                    aria-label={field.name}
                >
                    <option value="">Select an Option</option>
                    {Array.isArray(field.options) &&
                        field.options.map((opt, idx) => {
                            if (opt == null) return null;
                            if (typeof opt === "object") {
                                const key = opt.value ?? opt.text ?? `${field.id}-opt-${idx}`;
                                const val = opt.value ?? opt.text ?? key;
                                const txt = opt.text ?? opt.value ?? String(opt);
                                return <option key={key} value={val}>{txt}</option>;
                            }
                            return <option key={`${field.id}-opt-${idx}`} value={opt}>{opt}</option>;
                        })}
                </select>
            );
        }
        if (field.datatype === "date") {
            return (
                <input
                    id={`field-${field.id}`}
                    type="date"
                    className="form-input w-full p-1 border rounded"
                    value={value ?? ""}
                    readOnly={!!field.readonly}
                    onChange={(e) => updateLocal(field.id, e.target.value)}
                />
            );
        }
        return (
            <input
                id={`field-${field.id}`}
                type="text"
                className="form-input w-full p-1 border rounded"
                value={value ?? ""}
                onChange={(e) => updateLocal(field.id, e.target.value)}
            />
        );
    };

    const renderRow = (rowFields, rowKey) => {
        if (
            rowFields.length === 2 &&
            rowFields.some((f) => f?.layout?.col === "left") &&
            rowFields.some((f) => f?.layout?.col === "right")
        ) {
            const left = rowFields.find((f) => f?.layout?.col === "left");
            const right = rowFields.find((f) => f?.layout?.col === "right");
            const leftValue = localFieldValues[left.id] ?? "";
            const rightValue = localFieldValues[right.id] ?? "";

            return (
                <div key={rowKey} className="flex items-start gap-4 py-1">
                    <div className="w-1/3">
                        {!left.hideLabel && <label className="block text-sm mb-1">{left.name}</label>}
                        {renderControl(left, leftValue)}
                    </div>

                    <div className="w-1/3">
                        {!right.hideLabel && <label className="block text-sm mb-1">{right.name}</label>}
                        {renderControl(right, rightValue)}
                    </div>

                    <div className="w-1/3" aria-hidden="true" />
                </div>
            );
        }

        return rowFields.map((field) => {
            const value = localFieldValues[field.id] ?? "";
            if (field.control === "label" || field.control === "static") {
                return (
                    <div key={field.id} className="py-2">
                        <div className="form-label block text-sm">{field.name}</div>
                    </div>
                );
            }
            return (
                <div key={field.id} className="flex items-center justify-between gap-4 py-1">
                    <div className="w-1/3">
                        {!field.hideLabel && <label className="form-label block text-sm">{field.name}</label>}
                    </div>
                    <div className="w-1/3">{renderControl(field, value)}</div>
                    <div className="w-1/3" aria-hidden="true" />
                </div>
            );
        });
    };
    return (
        <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
            <div className="section-header px-4 py-2">Cash to Close</div>
            <div className="px-4 py-4 space-y-1">
                {[...rows.entries()].map(([rowKey, fieldsInRow]) => renderRow(fieldsInRow, rowKey))}
            </div>
        </div>
    );
};

export default CashToCloseControls;
